import React from 'react';
import styled from 'styled-components';
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GlobalFonts } from '../../styles/GlobalFonts';
import { secondPracticaLocales } from '../../locales/firstScreen';
import { useSwipeable } from 'react-swipeable';
import { Footer } from '../UI/Footer/Footer';

interface SecondPractica1Props {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onNavigateToThreeScreen: () => void;
  toHome?: () => void;
}

const slideVariants = {
  enterFromRight: {
    x: '100%',
    opacity: 0,
  },
  enterFromLeft: {
    x: '-100%',
    opacity: 0,
  },
  center: {
    x: 0,
    opacity: 1,
  },
  exitToLeft: {
    x: '-100%',
    opacity: 0,
  },
  exitToRight: {
    x: '100%',
    opacity: 0,
  },
};

const transition = {
  duration: 0.3,
  ease: [0.4, 0, 0.2, 1],
};

const Container = styled.div`
  background: #151515;
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  padding: 0 10px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: none;
  user-select: none;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 40px;
  font-weight: 600;
  margin-bottom: 8px;
  margin-top: 1px;
  line-height: 100%;
`;

const TheoryButton = styled.button`
  background: none;
  border: 2px solid #ffffff;
  border-radius: 12px;
  padding: 5px 7px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  line-height: 100%;
`;

const Section = styled.div`
  margin: 0 0 30px;
  display: flex;
  flex-direction: column;
`;

const SectionText = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  text-align: center;
  margin-bottom: 10px;

  @media (max-height: 375px) {
    font-size: 10px;
    margin-bottom: 5px;
  }
`;

const HighlightBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 288px;
  padding: 11px 0;
  margin: 0 auto;
`;

const HighlightText = styled.span`
  color: #7354f2;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  margin: 0 9px;
  white-space: pre-line;
  line-height: 1.4;
  text-shadow:
    0 0 5px rgba(115, 84, 242, 0.4),
    0 0 10px rgba(115, 84, 242, 0.3),
    0 0 15px rgba(115, 84, 242, 0.2),
    0 0 20px rgba(115, 84, 242, 0.15),
    0 0 25px rgba(115, 84, 242, 0.1);
`;

const SwapContainer = styled.div`
  background: #ffffff0d;
  padding: 20px 0 20px;
  border-radius: 12px;
  width: 100%;
  margin: 0;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-height: 667px) {
    padding: 15px 0 15px;
  }
`;

const SwapIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1px 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -15px;
    right: -15px;
    height: 1px;
    background-color: #303030;
    z-index: 0;
  }

  img {
    position: relative;
    z-index: 1;
  }
`;

const SwapInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 15px;

  @media (max-height: 667px) {
    gap: 0px;
  }
`;

const SwapRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;

  @media (max-height: 667px) {
    padding: 2px 0;
  }
`;

const SwapLabel = styled.span`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
`;

const SwapValue = styled.span<{ $hasError?: boolean }>`
  color: ${(props) => (props.$hasError ? '#ff4444' : '#ffffff')};
  font-family: 'Manrope', sans-serif;
  font-size: 18px;
  font-weight: 600;
`;

const SwapInput = styled.input<{ $hasError?: boolean }>`
  color: ${(props) => (props.$hasError ? '#ff4444' : '#ffffff')};
  font-family: 'Manrope', sans-serif;
  font-size: 18px;
  font-weight: 600;
  background: transparent;
  border: none;
  outline: none;
  text-align: right;
  width: 100px;

  &::placeholder {
    color: #666666;
  }

  /* Remove spinner buttons in number input */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    appearance: textfield;
    -moz-appearance: textfield;
  }
`;

const SwapValue2 = styled.span`
  color: #b5b5b5;
  font-family: 'Manrope', sans-serif;
  font-size: 12px;
  font-weight: 500;
`;

const TokenImage = styled.img`
  width: 30px;
  height: 30px;
  margin-right: 10px;

  @media (max-height: 375px) {
    width: 16px;
    height: 16px;
  }
`;

const InputButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  text-align: center;
  padding: 18px 21px;
  margin-bottom: 10px;
  margin-top: 10px;
  border-radius: 12px;
  border: 2px solid rgba(180, 180, 180, 0.2);
  background: none;
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 13px;
  font-weight: 600;
  width: 100%;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SwitchButton = styled.button`
  background-color: transparent;
  padding: 0;
`;

const QuestionBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  padding: 0;
  text-align: center;
  position: relative;
  width: fit-content;
`;

const QuestionText = styled.span`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 26px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 90%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  height: 90px;
  margin: 50px 0 30px;
  padding: 13px;
  border-radius: 20px;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(/assets/back2.gif) center/cover no-repeat;
    mix-blend-mode: normal;
    opacity: 0.9;
    border-radius: 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #7354f2;
    opacity: 1;
    mix-blend-mode: multiply;
    border-radius: 20px;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const TokenButton = styled.button`
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: #232323;
  width: 100%;
  max-width: 380px;
  border-radius: 20px;
  padding: 16px;
  margin: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalContent2 = styled(motion.div)`
  background: #7354f2;
  width: 100%;
  max-width: 380px;
  border-radius: 20px;
  padding: 16px;
  margin: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ErrorModalContent = styled(motion.div)`
  background: #232323;
  width: 100%;
  max-width: 380px;
  border-radius: 20px;
  padding: 30px 20px;
  margin: 10px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalTitle = styled.h3`
  font-family: 'Manrope', sans-serif;
  font-size: 28px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px 0;
  text-align: center;
`;

const ModalText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #ffffff;
  text-align: center;
  line-height: 145%;
  margin: 0 0 16px 0;
`;

const TokenSelectButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: #232323;
  border: 2px solid #b4b4b433;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 10px;
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 22px;
  right: 22px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  width: 12px;
  height: 12px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 15px;
    height: 2px;
    background: #ffffff;
    top: 50%;
    left: 50%;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const ScrollContainer = styled.div<{ $keyboardHeight?: number }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overflow-x: hidden;
  position: relative;
  padding-bottom: ${(props) =>
    props.$keyboardHeight ? props.$keyboardHeight + 100 : 100}px;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
  z-index: 10;
`;

const LANG_KEY = 'stonpools-lang';

const SecondPractica1: React.FC<SecondPractica1Props> = ({
  onNavigateToThreeScreen,
  onSwipeLeft,
  onSwipeRight,
  toHome,
}) => {
  const [isTokenAModalOpen, setIsTokenAModalOpen] = useState(false);
  const [isTokenBModalOpen, setIsTokenBModalOpen] = useState(false);
  const [selectedTokenA, setSelectedTokenA] = useState('');
  const [selectedTokenB, setSelectedTokenB] = useState('');
  const [isSwapEnabled, setIsSwapEnabled] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const [showError, setShowError] = useState(false);
  const [sendValue, setSendValue] = useState('0.00');
  const [receiveValue, setReceiveValue] = useState('0.00');
  const [showDuplicateError, setShowDuplicateError] = useState(false);
  const [showInsufficientBalanceError, setShowInsufficientBalanceError] =
    useState(false);
  const [showZeroAmountError, setShowZeroAmountError] = useState(false);
  const [hasBalanceError, setHasBalanceError] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const tokens = [
    { id: 'A', name: 'TOKEN A', balance: '0', image: '/assets/tokenA.png' },
    { id: 'B', name: 'TOKEN B', balance: '10', image: '/assets/tokenB.png' },
    { id: 'C', name: 'TOKEN C', balance: '0', image: '/assets/tokenC.png' },
  ];

  // Функция для получения баланса токена
  const getTokenBalance = (tokenId: string): number => {
    const token = tokens.find((t) => t.id === tokenId);
    return token ? parseFloat(token.balance) : 0;
  };

  // Обработчик изменения значения sendValue
  const handleSendValueChange = (value: string) => {
    // Разрешаем пустую строку, числа и десятичные дроби
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setSendValue(value);

      const numValue = parseFloat(value) || 0;
      const balance = getTokenBalance(selectedTokenB);

      // Проверка на превышение баланса (только меняем цвет, модалку не показываем)
      if (numValue > balance && balance > 0) {
        setHasBalanceError(true);
      } else {
        setHasBalanceError(false);
      }

      // Пересчитываем receiveValue на основе нового sendValue
      if (selectedTokenB === 'B' && selectedTokenA) {
        if (selectedTokenA === 'A') {
          const receiveAmount = (numValue * 1.7) / 10;
          setReceiveValue(
            receiveAmount > 0 ? receiveAmount.toFixed(2) : '0.00'
          );
        } else if (selectedTokenA === 'C') {
          const receiveAmount = (numValue * 1.4) / 10;
          setReceiveValue(
            receiveAmount > 0 ? receiveAmount.toFixed(2) : '0.00'
          );
        }
      } else if (selectedTokenA === 'B' && selectedTokenB) {
        // Если токен A = B, то вычисляем sendValue
        if (selectedTokenB === 'A') {
          const receiveAmount = (numValue * 10) / 1.7;
          setReceiveValue(
            receiveAmount > 0 ? receiveAmount.toFixed(2) : '0.00'
          );
        } else if (selectedTokenB === 'C') {
          const receiveAmount = (numValue * 10) / 1.4;
          setReceiveValue(
            receiveAmount > 0 ? receiveAmount.toFixed(2) : '0.00'
          );
        }
      }
    }
  };

  const handleTokenSelect = (tokenId: string, isTokenA: boolean) => {
    if (isTokenA && tokenId === selectedTokenB) {
      setIsTokenAModalOpen(false);
      setShowDuplicateError(true);
      return;
    }
    if (!isTokenA && tokenId === selectedTokenA) {
      setIsTokenBModalOpen(false);
      setShowDuplicateError(true);
      return;
    }

    const newTokenA = isTokenA ? tokenId : selectedTokenA;
    const newTokenB = isTokenA ? selectedTokenB : tokenId;

    if (isTokenA) {
      setSelectedTokenA(tokenId);
      setIsTokenAModalOpen(false);
    } else {
      setSelectedTokenB(tokenId);
      setIsTokenBModalOpen(false);
    }

    // Сбрасываем ошибки при смене токена
    setHasBalanceError(false);

    // Получаем баланс выбранного токена "from"
    const balanceFrom = getTokenBalance(newTokenB);
    const sendAmount = balanceFrom.toString();

    if (newTokenB && newTokenA) {
      setSendValue(sendAmount);

      // Рассчитываем receiveValue на основе баланса
      const numValue = balanceFrom;

      if (newTokenB === 'B') {
        if (newTokenA === 'A') {
          const receiveAmount = (numValue * 1.7) / 10;
          setReceiveValue(
            receiveAmount > 0 ? receiveAmount.toFixed(2) : '0.00'
          );
        } else if (newTokenA === 'C') {
          const receiveAmount = (numValue * 1.4) / 10;
          setReceiveValue(
            receiveAmount > 0 ? receiveAmount.toFixed(2) : '0.00'
          );
        } else {
          setReceiveValue('0.00');
        }
      } else if (newTokenA === 'B') {
        const balanceTo = getTokenBalance(newTokenA);
        setReceiveValue(balanceTo.toString());

        if (newTokenB === 'A') {
          const sendCalc = (balanceTo * 1.7) / 10;
          setSendValue(sendCalc > 0 ? sendCalc.toFixed(2) : '0.00');
        } else if (newTokenB === 'C') {
          const sendCalc = (balanceTo * 1.4) / 10;
          setSendValue(sendCalc > 0 ? sendCalc.toFixed(2) : '0.00');
        }
      } else {
        // Если ни один из токенов не B, устанавливаем баланс from токена
        setSendValue(sendAmount);
        setReceiveValue('0.00');
      }
    } else {
      setSendValue(sendAmount);
      setReceiveValue('0.00');
    }

    setIsSwapEnabled(
      newTokenA !== '' && newTokenB !== '' && newTokenA !== newTokenB
    );
  };

  const switchValues = () => {
    if (isSwapEnabled) {
      const valA = selectedTokenA;
      const valB = selectedTokenB;
      const sendVal = sendValue;
      const recVal = receiveValue;

      // Меняем токены местами
      setSelectedTokenA(valB);
      setSelectedTokenB(valA);
      setSendValue(recVal);
      setReceiveValue(sendVal);

      // Проверяем новое значение после переключения
      // После инверсии токен который был "to" стал "from", поэтому проверяем баланс valB
      const numValue = parseFloat(recVal) || 0;
      const newBalanceFrom = getTokenBalance(valB);

      // Валидация: проверяем, не превышает ли новое sendValue баланс нового "from" токена
      if (numValue > newBalanceFrom && newBalanceFrom > 0) {
        setHasBalanceError(true);
      } else {
        setHasBalanceError(false);
      }
    }
    return false;
  };

  const handleSwap = () => {
    // Проверяем на пустое или нулевое значение
    const numValue = parseFloat(sendValue) || 0;
    if (
      numValue === 0 ||
      sendValue === '' ||
      sendValue === '0' ||
      sendValue === '0.00'
    ) {
      setShowZeroAmountError(true);
      return;
    }

    // Проверяем на ошибку баланса
    if (hasBalanceError) {
      setShowInsufficientBalanceError(true);
      return;
    }

    if (selectedTokenB !== 'B') {
      setShowError(true);
      return;
    }
    setShowCongrats(true);
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => onSwipeLeft?.(),
    onSwipedRight: () => onSwipeRight?.(),
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
  });

  // Обработка появления клавиатуры
  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const keyboardHeight = windowHeight - viewportHeight;
        setKeyboardHeight(Math.max(0, keyboardHeight));
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      handleResize();

      return () => {
        window.visualViewport?.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  // Обработчик фокуса на поле ввода
  const handleInputFocus = () => {
    // Даем время клавиатуре появиться
    setTimeout(() => {
      if (inputRef.current && scrollContainerRef.current) {
        // Прокручиваем к полю ввода
        inputRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // Дополнительная прокрутка контейнера для обеспечения видимости
        setTimeout(() => {
          if (inputRef.current && scrollContainerRef.current) {
            const inputRect = inputRef.current.getBoundingClientRect();
            const containerRect =
              scrollContainerRef.current.getBoundingClientRect();
            const viewportHeight =
              window.visualViewport?.height || window.innerHeight;
            const inputBottom = inputRect.bottom;
            const visibleAreaBottom = viewportHeight;

            // Если поле ввода перекрыто клавиатурой, прокручиваем дополнительно
            if (inputBottom > visibleAreaBottom - 20) {
              const scrollAmount = inputBottom - visibleAreaBottom + 100;
              scrollContainerRef.current.scrollBy({
                top: scrollAmount,
                behavior: 'smooth',
              });
            }
          }
        }, 100);
      }
    }, 300);
  };

  // Получаем язык и локализацию как в SecondTheory1
  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = secondPracticaLocales[lang];

  return (
    <div {...handlers}>
      <motion.div
        key="secondPractica1"
        initial="enterFromRight"
        animate="center"
        exit="exitToLeft"
        variants={slideVariants}
        transition={transition}
        style={{
          width: '100%',
          height: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          msUserSelect: 'none',
          overscrollBehavior: 'none',
        }}
      >
        <GlobalFonts />
        <Container>
          <HomeButton onClick={toHome}>
            <HomeButtonIcon src="/assets/homeIconPurple.svg" />
          </HomeButton>
          <ScrollContainer
            ref={scrollContainerRef}
            $keyboardHeight={keyboardHeight}
          >
            <Header>
              <Title>{t.title}</Title>
              <TheoryButton>{t.swapButton}</TheoryButton>
            </Header>
            <Section>
              <QuestionBlock>
                <QuestionText>{t.question}</QuestionText>
              </QuestionBlock>
              <SectionText>{t.description}</SectionText>
              <HighlightBox>
                <HighlightText>{t.highlight}</HighlightText>
              </HighlightBox>
            </Section>
            <SwapContainer>
              <SwapInnerContainer>
                <SwapRow>
                  <SwapLabel>{t.from}</SwapLabel>
                  <SwapValue2>
                    {selectedTokenB === 'B' ? '10' : '0.00'}
                  </SwapValue2>
                </SwapRow>
                <SwapRow>
                  <TokenButton onClick={() => setIsTokenBModalOpen(true)}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <TokenImage
                        src={
                          selectedTokenB
                            ? tokens.find((t) => t.id === selectedTokenB)?.image
                            : tokens[1].image
                        }
                        alt="Token"
                      />
                      <SwapValue>
                        {selectedTokenB
                          ? `TOKEN ${selectedTokenB}`
                          : t.selectToken}
                      </SwapValue>
                    </div>
                  </TokenButton>
                  <SwapInput
                    ref={inputRef}
                    type="text"
                    value={sendValue}
                    onChange={(e) => handleSendValueChange(e.target.value)}
                    onFocus={handleInputFocus}
                    placeholder="0.00"
                    $hasError={hasBalanceError}
                  />
                </SwapRow>
                <SwapIconContainer>
                  <SwitchButton onClick={switchValues}>
                    <img
                      src="/assets/swap.svg"
                      alt="swap"
                      style={{
                        width: '32px',
                        height: '32px',
                      }}
                    />
                  </SwitchButton>
                </SwapIconContainer>
                <SwapRow>
                  <SwapLabel>{t.to}</SwapLabel>
                  <SwapValue2>
                    {selectedTokenA === 'B' ? '10' : '0.00'}
                  </SwapValue2>
                </SwapRow>
                <SwapRow>
                  <TokenButton onClick={() => setIsTokenAModalOpen(true)}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <TokenImage
                        src={
                          selectedTokenA
                            ? tokens.find((t) => t.id === selectedTokenA)?.image
                            : tokens[0].image
                        }
                        alt="Token"
                      />
                      <SwapValue>
                        {selectedTokenA
                          ? `TOKEN ${selectedTokenA}`
                          : t.selectToken}
                      </SwapValue>
                    </div>
                  </TokenButton>
                  <SwapValue>{receiveValue}</SwapValue>
                </SwapRow>
              </SwapInnerContainer>
              <InputButton
                onClick={handleSwap}
                disabled={!isSwapEnabled}
                style={{
                  background: isSwapEnabled ? 'transparent' : 'transparent',
                }}
              >
                {isSwapEnabled ? t.swapButton : t.selectToken}
              </InputButton>
            </SwapContainer>
            <Footer
              practica
              textLeft={t.back}
              onSwipeRight={onSwipeRight}
              onSwipeLeft={onSwipeLeft}
            />
          </ScrollContainer>
        </Container>

        <AnimatePresence>
          {isTokenAModalOpen && (
            <ModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsTokenAModalOpen(false)}
            >
              <ModalContent
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <CloseButton onClick={() => setIsTokenAModalOpen(false)} />
                <ModalTitle>{t.selectToken}</ModalTitle>
                {tokens.map((token) => (
                  <TokenSelectButton
                    key={token.id}
                    onClick={() => handleTokenSelect(token.id, true)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <TokenImage src={token.image} alt={token.name} />
                      <span>{token.name}</span>
                    </div>
                    <span>{token.balance}</span>
                  </TokenSelectButton>
                ))}
              </ModalContent>
            </ModalOverlay>
          )}

          {isTokenBModalOpen && (
            <ModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsTokenBModalOpen(false)}
            >
              <ModalContent
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <CloseButton onClick={() => setIsTokenBModalOpen(false)} />
                <ModalTitle>{t.selectToken}</ModalTitle>
                {tokens.map((token) => (
                  <TokenSelectButton
                    key={token.id}
                    onClick={() => handleTokenSelect(token.id, false)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <TokenImage src={token.image} alt={token.name} />
                      <span>{token.name}</span>
                    </div>
                    <span>{token.balance}</span>
                  </TokenSelectButton>
                ))}
              </ModalContent>
            </ModalOverlay>
          )}

          {showCongrats && (
            <ModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ModalContent2
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <CloseButton onClick={() => setShowCongrats(false)} />
                <ModalTitle>{t.successModal.title}</ModalTitle>
                <ModalText>{t.successModal.text}</ModalText>
                <InputButton
                  onClick={() => {
                    setShowCongrats(false);
                    onNavigateToThreeScreen?.();
                  }}
                >
                  {t.successModal.next}
                </InputButton>
              </ModalContent2>
            </ModalOverlay>
          )}

          {showError && (
            <ModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowError(false)}
            >
              <ErrorModalContent
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <CloseButton onClick={() => setShowError(false)} />
                <ModalTitle>{t.errorModal.title}</ModalTitle>
                <ModalText>{t.errorModal.text}</ModalText>
                <InputButton onClick={() => setShowError(false)}>
                  {t.errorModal.close}
                </InputButton>
              </ErrorModalContent>
            </ModalOverlay>
          )}

          {showDuplicateError && (
            <ModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDuplicateError(false)}
            >
              <ErrorModalContent
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <CloseButton onClick={() => setShowDuplicateError(false)} />
                <ModalTitle>{t.errorModal.title}</ModalTitle>
                <ModalText>{t.duplicateError}</ModalText>
                <InputButton onClick={() => setShowDuplicateError(false)}>
                  {t.errorModal.close}
                </InputButton>
              </ErrorModalContent>
            </ModalOverlay>
          )}

          {showInsufficientBalanceError && (
            <ModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowInsufficientBalanceError(false)}
            >
              <ErrorModalContent
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <CloseButton
                  onClick={() => setShowInsufficientBalanceError(false)}
                />
                <ModalTitle>{t.errorModal.title}</ModalTitle>
                <ModalText>{t.insufficientBalanceError}</ModalText>
                <InputButton
                  onClick={() => setShowInsufficientBalanceError(false)}
                >
                  {t.errorModal.close}
                </InputButton>
              </ErrorModalContent>
            </ModalOverlay>
          )}

          {showZeroAmountError && (
            <ModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowZeroAmountError(false)}
            >
              <ErrorModalContent
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <CloseButton onClick={() => setShowZeroAmountError(false)} />
                <ModalTitle>{t.errorModal.title}</ModalTitle>
                <ModalText>{t.zeroAmountError}</ModalText>
                <InputButton onClick={() => setShowZeroAmountError(false)}>
                  {t.errorModal.close}
                </InputButton>
              </ErrorModalContent>
            </ModalOverlay>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default SecondPractica1;
